import React, { useState } from "react";
import { useGoogleLogin } from "@react-oauth/google";
import config from "../config";

// ─────────────────────────────────────────────────────────────
// HARDCODED SUPERADMIN — walang database needed
// ─────────────────────────────────────────────────────────────
const SUPERADMIN = {
  username: "superadmin",
  password: "superadmin",
  role: "superadmin",
  fullNames: "Super Administrator",
  email: "superadmin@system.local",
};

const Login = ({ setIsLoggedIn, setCurrentPage, setUserRole }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showRegister, setShowRegister] = useState(false);

  const [showPasswordSetup, setShowPasswordSetup] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [googleUserData, setGoogleUserData] = useState(null);

  const generateGooglePassword = () =>
    Math.random().toString(36).slice(-12) + Date.now().toString(36);

  const downloadGoogleProfilePicture = async (pictureUrl) => {
    try {
      const response = await fetch(pictureUrl);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error("❌ Failed to download profile picture:", error);
      return null;
    }
  };

  const uploadProfilePicture = async (username, base64Data) => {
    try {
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'image/jpeg' });
      const formData = new FormData();
      formData.append('file', blob, 'profile.jpg');
      const response = await fetch(
        `${config.API_BASE_URL}/Users/upload-profile-picture?username=${username}`,
        { method: 'POST', body: formData }
      );
      return response.ok;
    } catch (error) {
      console.error("❌ Failed to upload profile picture:", error);
      return false;
    }
  };

  // ── Set IsOnline = true for admin ─────────────────────────
  const setAdminOnline = async (username) => {
    try {
      const formData = new URLSearchParams();
      formData.append("username", username);
      await fetch(`${config.API_BASE_URL}/Admin/login?username=${username}&password=__online_update__`, {
        method: "GET",
      });
      // We already set online inside the login endpoint on success,
      // so no extra call needed — handled by the login response itself.
    } catch (err) {
      console.warn("⚠️ Could not set online status:", err);
    }
  };

  // ── Google Login ──────────────────────────────────────────
  const loginWithGoogle = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setLoading(true);
      setError("");
      try {
        const userInfoRes = await fetch(
          "https://www.googleapis.com/oauth2/v3/userinfo",
          { headers: { Authorization: `Bearer ${tokenResponse.access_token}` } }
        );
        if (!userInfoRes.ok) throw new Error("Failed to get user info from Google");
        const userInfo = await userInfoRes.json();

        const userEmail = userInfo.email;
        const userName = userInfo.name;
        const profilePicUrl = userInfo.picture;

        const checkUserRes = await fetch(
          `${config.API_BASE_URL}/Users/check-user?email=${encodeURIComponent(userEmail)}`
        );

        if (checkUserRes.ok) {
          const userData = await checkUserRes.json();
          if (userData.googlePassword) {
            const loginRes = await fetch(
              `${config.API_BASE_URL}/Users/google-login?email=${encodeURIComponent(userEmail)}&googlePassword=${encodeURIComponent(userData.googlePassword)}`
            );
            if (loginRes.ok) {
              const loggedInUser = await loginRes.json();
              const userDataWithPicture = { ...loggedInUser, googleProfilePicture: profilePicUrl };
              if (!loggedInUser.password || loggedInUser.password === "") {
                setGoogleUserData(userDataWithPicture);
                setShowPasswordSetup(true);
                setLoading(false);
                return;
              }
              localStorage.setItem("isLoggedIn", "true");
              localStorage.setItem("userRole", "user");
              localStorage.setItem("user", JSON.stringify(userDataWithPicture));
              localStorage.setItem("currentPage", "user-dashboard");
              setIsLoggedIn(true);
              setUserRole("user");
              setCurrentPage("user-dashboard");
            } else {
              throw new Error("Google login failed");
            }
          }
        } else if (checkUserRes.status === 404) {
          const googlePassword = generateGooglePassword();
          let profilePictureBase64 = null;
          if (profilePicUrl) profilePictureBase64 = await downloadGoogleProfilePicture(profilePicUrl);

          const formData = new URLSearchParams();
          formData.append("username", userEmail);
          formData.append("password", "");
          formData.append("fullNames", userName || "Google User");
          formData.append("email", userEmail);
          formData.append("googlePassword", googlePassword);

          const registerRes = await fetch(`${config.API_BASE_URL}/Users/register`, {
            method: "POST", body: formData,
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
          });
          if (!registerRes.ok) throw new Error("Failed to register user: " + await registerRes.text());
          if (profilePictureBase64) await uploadProfilePicture(userEmail, profilePictureBase64);

          const newUserData = {
            username: userEmail, email: userEmail,
            fullNames: userName || "Google User",
            googlePassword, password: "",
            googleProfilePicture: profilePicUrl,
          };
          setGoogleUserData(newUserData);
          setShowPasswordSetup(true);
        } else {
          throw new Error("Failed to check user existence");
        }
      } catch (err) {
        console.error("❌ Google login error:", err);
        setError("Google login failed: " + err.message);
      } finally {
        setLoading(false);
      }
    },
    onError: (err) => {
      console.error("❌ Google OAuth error:", err);
      setError("Google login failed. Please try again.");
    },
  });

  // ── Set Manual Password (Google users) ───────────────────
  const handleSetPassword = (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { setError("Passwords do not match!"); return; }
    if (newPassword.length < 6) { setError("Password must be at least 6 characters!"); return; }
    setLoading(true);
    setError("");
    const formData = new URLSearchParams();
    formData.append("username", googleUserData.username);
    formData.append("password", newPassword);
    fetch(`${config.API_BASE_URL}/Users/set-password`, {
      method: "POST", body: formData,
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    })
      .then(res => { if (!res.ok) throw new Error("Failed to set password"); return res.json(); })
      .then(() => {
        alert("✅ Password set successfully! You can now login manually.");
        const updatedUser = { ...googleUserData, password: newPassword };
        localStorage.setItem("isLoggedIn", "true");
        localStorage.setItem("userRole", "user");
        localStorage.setItem("user", JSON.stringify(updatedUser));
        localStorage.setItem("currentPage", "user-dashboard");
        setIsLoggedIn(true); setUserRole("user"); setCurrentPage("user-dashboard");
      })
      .catch(err => { setError(err.message); })
      .finally(() => setLoading(false));
  };

  // ── Manual Login ──────────────────────────────────────────
  const handleLogin = (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    // ✅ Check superadmin FIRST — hardcoded, walang API call
    if (
      username.trim().toLowerCase() === SUPERADMIN.username &&
      password === SUPERADMIN.password
    ) {
      const superadminData = {
        username: SUPERADMIN.username,
        fullNames: SUPERADMIN.fullNames,
        email: SUPERADMIN.email,
        role: "superadmin",
      };
      localStorage.setItem("isLoggedIn", "true");
      localStorage.setItem("userRole", "superadmin");
      localStorage.setItem("admin", JSON.stringify(superadminData));
      localStorage.setItem("currentPage", "dashboard");
      setIsLoggedIn(true);
      setUserRole("superadmin");
      setCurrentPage("dashboard");
      setLoading(false);
      return;
    }

    // ✅ Admin login — login endpoint na mismo nag-se-set ng IsOnline = true sa backend
    fetch(`${config.API_BASE_URL}/Admin/login?username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}`)
      .then(res => {
        if (res.ok) return res.json().then(data => ({ data, userType: "admin" }));
        // fallback to user login
        return fetch(`${config.API_BASE_URL}/Users/login?username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}`)
          .then(userRes => {
            if (userRes.ok) return userRes.json().then(data => ({ data, userType: "user" }));
            throw new Error("Invalid username or password");
          });
      })
      .then(({ data, userType }) => {
        localStorage.setItem("isLoggedIn", "true");

        if (userType === "admin") {
          const actualRole = data.role || "Admin";
          const normalizedRole = actualRole.toLowerCase().replace(/\s+/g, '_');

          const adminData = {
            ...data,
            role: actualRole,
            claimUsername: data.identificationCode || data.username,
            isOnline: true, // ✅ reflect online status in localStorage
          };

          localStorage.setItem("userRole", normalizedRole);
          localStorage.setItem("admin", JSON.stringify(adminData));
          localStorage.setItem("currentPage", "dashboard");
          setCurrentPage("dashboard");
          setUserRole(normalizedRole);
        } else {
          localStorage.setItem("userRole", "user");
          localStorage.setItem("user", JSON.stringify(data));
          localStorage.setItem("currentPage", "user-dashboard");
          setCurrentPage("user-dashboard");
          setUserRole("user");
        }

        localStorage.setItem("isLoggedIn", "true");
        setIsLoggedIn(true);
      })
      .catch(err => { setError(err.message); })
      .finally(() => setLoading(false));
  };

  // ── Manual Registration ───────────────────────────────────
  const handleRegister = (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const formData = new URLSearchParams();
    formData.append("username", username);
    formData.append("password", password);
    formData.append("fullNames", fullName);
    formData.append("email", email);
    formData.append("googlePassword", "");
    fetch(`${config.API_BASE_URL}/Users/register`, {
      method: "POST", body: formData,
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    })
      .then(res => {
        if (!res.ok) return res.text().then(text => { throw new Error(text || "Registration failed"); });
        return res.json();
      })
      .then(() => {
        alert("✅ Registration successful! Please login.");
        setShowRegister(false);
        setUsername(""); setPassword(""); setFullName(""); setEmail("");
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  };

  // ── Branding Footer ───────────────────────────────────────
  const BrandingFooter = () => (
    <div className="mt-8 pt-5 border-t border-gray-700/60">
      <div className="flex flex-col items-center gap-1">
        <p className="text-gray-500 text-xs tracking-widest uppercase font-medium">Powered By</p>
        <p className="text-white font-bold text-sm tracking-wide">
          <span className="text-blue-400">QS</span> I.T SERVICES
        </p>
        <div className="flex items-center gap-2 mt-1">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500"></span>
          <p className="text-gray-400 text-xs font-medium">Version 1.0</p>
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500"></span>
        </div>
        <p className="text-gray-600 text-xs mt-1">© 2025 All rights reserved</p>
      </div>
    </div>
  );

  // ── Password Setup Modal ──────────────────────────────────
  if (showPasswordSetup) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <div className="bg-gray-800 p-8 rounded-3xl shadow-2xl w-full max-w-md">
          <div className="text-center mb-6">
            {googleUserData?.googleProfilePicture && (
              <img src={googleUserData.googleProfilePicture} alt="Profile"
                className="w-20 h-20 rounded-full mx-auto mb-4 border-4 border-blue-500" />
            )}
            <div className="bg-blue-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Welcome, {googleUserData?.fullNames}!</h2>
            <p className="text-gray-400">Create a password to enable manual login</p>
          </div>
          {error && <div className="bg-red-500/10 border border-red-500 text-red-400 px-4 py-3 rounded-lg mb-4">{error}</div>}
          <div className="space-y-4">
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">New Password</label>
              <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                className="w-full p-3 rounded-lg bg-gray-700 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                placeholder="Enter new password (min 6 chars)" minLength={6} />
            </div>
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">Confirm Password</label>
              <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                className="w-full p-3 rounded-lg bg-gray-700 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                placeholder="Confirm password" minLength={6} />
            </div>
            <button onClick={handleSetPassword} disabled={loading}
              className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700
                text-white p-3 rounded-lg font-bold transition transform hover:scale-105 disabled:opacity-50">
              {loading ? "Setting Password..." : "Set Password & Continue"}
            </button>
            <button onClick={() => {
              localStorage.setItem("isLoggedIn", "true");
              localStorage.setItem("userRole", "user");
              localStorage.setItem("user", JSON.stringify(googleUserData));
              localStorage.setItem("currentPage", "user-dashboard");
              setIsLoggedIn(true); setUserRole("user"); setCurrentPage("user-dashboard");
            }} className="w-full bg-gray-700 hover:bg-gray-600 text-gray-300 p-3 rounded-lg font-medium transition">
              Skip for Now
            </button>
          </div>
          <BrandingFooter />
        </div>
      </div>
    );
  }

  // ── Main Login / Register UI ──────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      <div className="bg-gray-800 p-8 rounded-3xl shadow-2xl w-full max-w-md">
        <div className="text-center mb-6">
          <h1 className="text-4xl font-bold text-white">
            {showRegister ? "Register" : "Login Portal"}
          </h1>
          {!showRegister && (
            <p className="text-gray-400 text-sm mt-2">Sign in to continue</p>
          )}
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500 text-red-400 px-4 py-3 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        {showRegister ? (
          <div className="space-y-4">
            <input type="text" placeholder="Full Name" value={fullName}
              onChange={e => setFullName(e.target.value)}
              className="w-full p-3 rounded-lg bg-gray-700 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none" />
            <input type="email" placeholder="Email" value={email}
              onChange={e => setEmail(e.target.value)}
              className="w-full p-3 rounded-lg bg-gray-700 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none" />
            <input type="text" placeholder="Username" value={username}
              onChange={e => setUsername(e.target.value)}
              className="w-full p-3 rounded-lg bg-gray-700 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none" />
            <input type="password" placeholder="Password" value={password}
              onChange={e => setPassword(e.target.value)}
              className="w-full p-3 rounded-lg bg-gray-700 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none" />
            <button onClick={handleRegister} disabled={loading}
              className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700
                text-white p-3 rounded-lg font-bold transition transform hover:scale-105 disabled:opacity-50">
              {loading ? "Registering..." : "Register"}
            </button>
            <p className="text-gray-300 text-sm text-center">
              Already have an account?{" "}
              <button onClick={() => { setShowRegister(false); setError(""); }}
                className="text-blue-400 hover:text-blue-300 font-semibold">Login</button>
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              <input type="text" value={username} placeholder="Username or Email"
                onChange={e => setUsername(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleLogin(e)}
                className="w-full p-3 rounded-lg bg-gray-700 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none" />
              <input type="password" value={password} placeholder="Password"
                onChange={e => setPassword(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleLogin(e)}
                className="w-full p-3 rounded-lg bg-gray-700 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none" />
              <button onClick={handleLogin} disabled={loading}
                className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700
                  text-white p-3 rounded-lg font-bold transition transform hover:scale-105 disabled:opacity-50">
                {loading ? "Signing in..." : "Sign In"}
              </button>
            </div>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-600"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-gray-800 text-gray-400">Or continue with</span>
              </div>
            </div>

            <button onClick={() => loginWithGoogle()} disabled={loading}
              className="w-full bg-white text-gray-900 p-3 rounded-lg flex items-center justify-center gap-3
                shadow-sm border border-gray-300 hover:bg-gray-100 transition transform hover:scale-105 disabled:opacity-50">
              <svg className="w-6 h-6" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              <span className="font-medium text-sm">{loading ? "Connecting..." : "Sign in with Google"}</span>
            </button>

            <p className="text-gray-300 text-sm text-center mt-4">
              Don't have an account?{" "}
              <button onClick={() => { setShowRegister(true); setError(""); }}
                className="text-green-400 hover:text-green-300 font-semibold">Register here</button>
            </p>
          </>
        )}

        <BrandingFooter />
      </div>
    </div>
  );
};

export default Login;